import { useState } from "react";
import { Box, Button, Modal, Stack, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import { submitAdminVisitForm } from "../../../Redux/actions/responseAction";
import { toastMessage } from "../../../utils/toast";
import { ModalBaseStyles, ModalHeader } from "../../Common/styles/modal";

const VisitSubmitButton = () => {
  const { t } = useTranslation("responses");
  const dispatch = useAppDispatch();
  const { id: studyId, participantId } = useParams();
  const [showConfirm, setShowConfirm] = useState(false);

  const currentCategory = useAppSelector(
    (state) => state.response.currentCategory,
  );
  const selectedForm = useAppSelector((state) => state.response.selectedForm);
  const canLock = useAppSelector((state) => state.response.canLock);
  const isResponseSubmitting = useAppSelector(
    (state) => state.response.fieldSubmitting,
  );
  const isResponseLoading = useAppSelector((state) => state.response.isLoading);

  // Gated on canLock (participant.lock_unlock), not the general edit
  // permission — Submit is a lock action, same as the form's Lock/Unlock
  // menu, so it's available to the same set of users regardless of whether
  // they can also edit answers.
  //
  // `!isSubmitted` IS THE FIX FOR A MISLEADING WARNING. Without it the button
  // stayed on screen after the form was submitted, and pressing it fell
  // through to the `readyToSubmit` guard below — which warns "Please complete
  // all required questions before submitting" on a form that is complete, was
  // submitted, and says so in its own header two inches above. `readyToSubmit`
  // means "there is something left to submit", and it is false once a form has
  // been submitted; the guard read that as "incomplete".
  //
  // A submitted form cannot be submitted again — `submitAdminSurveyForm`
  // already refused it on the survey path — so the honest thing is for the
  // action not to be offered.
  // `readyToSubmit` IS THE SERVER'S OWN ANSWER TO "CAN THIS BE SUBMITTED", and
  // the button now asks it before offering the action. The survey button in
  // `FormHeader.tsx` has always required it (`showSurveySubmit`); this one did
  // not, so it rendered whenever the form was unlocked and then refused the
  // press with "Please complete all required questions before submitting" — a
  // message the user reads next to a sidebar that says Completed.
  //
  // Hiding the button rather than warning on press is the honest shape: the
  // action was never going to succeed, because `submitAdminVisitForm` applies
  // the same flag. Nothing is lost, and the false statement is gone.
  //
  // NOTE FOR WHOEVER PICKS THIS UP: the underlying question is why the server
  // reports `readyToSubmit: false` for a form it also reports as complete.
  // `computeReadyToSubmitFlag` returns false when the attempt is submitted or
  // LOCKED, and false otherwise only when the attempt is not eligible. One
  // look at the form GET's response body settles which. That is a platform
  // question, not a randomization one.
  const showButton =
    currentCategory === "visit" &&
    !!selectedForm &&
    !selectedForm.locked &&
    !selectedForm.isSigned &&
    !selectedForm.isSubmitted &&
    !!selectedForm.readyToSubmit &&
    canLock;

  if (!showButton) {
    return null;
  }

  const openConfirm = () => {
    if (isResponseSubmitting || isResponseLoading) return;
    // Defensive: the button is hidden in this state, but a stale render must
    // not produce the "incomplete" warning on a form that is already in.
    if (selectedForm?.isSubmitted) return;
    if (!selectedForm?.readyToSubmit) {
      toastMessage("warning", t("SURVEY_SUBMIT_INCOMPLETE"));
      return;
    }
    setShowConfirm(true);
  };

  const confirmSubmit = () => {
    setShowConfirm(false);
    if (studyId && participantId) {
      dispatch(submitAdminVisitForm(studyId, participantId));
    }
  };

  return (
    <>
      <Button
        variant="contained"
        onClick={openConfirm}
        disabled={!!isResponseSubmitting}
      >
        {t("VISIT_SUBMIT_BUTTON")}
      </Button>
      <Modal open={showConfirm} onClose={() => setShowConfirm(false)}>
        <Box
          sx={{ ...ModalBaseStyles, height: "fit-content", minHeight: "20vh" }}
        >
          <ModalHeader
            title={t("VISIT_SUBMIT_CONFIRM_TITLE")}
            onCloseClick={() => setShowConfirm(false)}
          />
          <Typography variant="subtitle1">
            {t("VISIT_SUBMIT_CONFIRM_BODY")}
          </Typography>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            sx={{ marginTop: "auto" }}
            gap={1.5}
          >
            <Button variant="outlined" onClick={() => setShowConfirm(false)}>
              {t("CANCEL", { ns: "common" })}
            </Button>
            <Button
              variant="contained"
              onClick={confirmSubmit}
              disabled={!!isResponseSubmitting}
            >
              {t("VISIT_SUBMIT_BUTTON")}
            </Button>
          </Stack>
        </Box>
      </Modal>
    </>
  );
};

export default VisitSubmitButton;
